import { useTranslation } from 'next-i18next';
import { useLocale } from '@/hooks';
import { FunctionCard, FunctionCards } from '@/shared/components';
import { FEATURE_QR_IMAGE, getFeatureImage, SECTION_PADDING } from '../constants';

/**
 * 카드 배경/제목 색은 ZUS DESIGN.md 의 시맨틱 bg/text 매핑(bg-neutral·
 * bg-alternative·bg-strong-alternative 등)이 전부 gray·violet 계열이라 이
 * 보라색 액센트에 대응하는 시맨틱 클래스가 없다. Figma 도 시맨틱 별칭이
 * 아니라 원시 변수를 직접 바인딩(get_design_context/get_variable_defs 로
 * 노드별 확인)해서 세 카드를 서로 다른 보라 톤으로 의도적으로 구분한다 —
 * raw 클래스를 그대로 둔다.
 *
 * - CollabCard(25558:5114): bg `color/purple/50`(#f5edff) · title `color/purple/800`(#6221b5)
 * - UnlimitedCard(25528:10061): bg `color/purple/100`(#ebdbff) · title `color/violet/900`(#342c80)
 * - QrCard(25528:10084): bg `#f6f3fa`(변수 미바인딩 raw hex) · title `color/purple/900`(#501699)
 */
export function BoardLandingFeatures() {
  const {
    t
  } = useTranslation(['boardLanding']);
  return <section aria-labelledby="board-landing-features-heading" className={SECTION_PADDING} data-sentry-component="BoardLandingFeatures" data-sentry-source-file="BoardLandingFeatures.tsx">
      <div className="mx-auto flex w-full max-w-[1280px] flex-col items-center gap-lg min-[751px]:items-stretch min-[751px]:gap-2xl">
        <h2 id="board-landing-features-heading" className="px-[16px] text-center text-display-sm-extrabold text-default min-[751px]:text-left">
          {t('boardLanding:features.title')}
        </h2>

        <FunctionCards topElement={<CollabCard />} bottomLeftElement={<UnlimitedCard />} bottomRightElement={<QrCard />} data-sentry-element="FunctionCards" data-sentry-source-file="BoardLandingFeatures.tsx" />
      </div>
    </section>;
}
function CollabCard() {
  const {
    locale
  } = useLocale();
  const {
    t
  } = useTranslation(['boardLanding']);
  return <FunctionCard.HorizontalWrapper className="bg-purple-50" data-sentry-element="unknown" data-sentry-component="CollabCard" data-sentry-source-file="BoardLandingFeatures.tsx">
      <div className="flex-1">
        <div className="flex size-full flex-col items-center gap-sm px-lg py-xl min-[751px]:items-stretch min-[751px]:gap-lg min-[751px]:p-2xl min-[751px]:pr-0 min-[1025px]:p-3xl min-[1025px]:pr-0">
          {/* 제목 줄바꿈은 `\n` — 로케일마다 길이가 달라 CSS 로 흘린다. */}
          <FunctionCard.Title className="whitespace-pre-line text-purple-800" data-sentry-element="unknown" data-sentry-source-file="BoardLandingFeatures.tsx">
            {t('boardLanding:features.collab.title')}
          </FunctionCard.Title>
          <FunctionCard.Description data-sentry-element="unknown" data-sentry-source-file="BoardLandingFeatures.tsx">
            {t('boardLanding:features.collab.description')}
          </FunctionCard.Description>
        </div>
      </div>

      <div className="flex-1 min-[751px]:h-[310px] min-[1025px]:h-[374px]">
        <img src={getFeatureImage(locale, 'collab')} alt={t('boardLanding:features.collab.imageAlt')} width={624} height={374} loading="lazy" className="size-full min-[751px]:object-cover min-[751px]:object-left" />
      </div>
    </FunctionCard.HorizontalWrapper>;
}
function UnlimitedCard() {
  const {
    locale
  } = useLocale();
  const {
    t
  } = useTranslation(['boardLanding']);
  return <FunctionCard.VerticalWrapper className="bg-purple-100" data-sentry-element="unknown" data-sentry-component="UnlimitedCard" data-sentry-source-file="BoardLandingFeatures.tsx">
      <div className="flex flex-col items-center gap-sm px-lg py-xl min-[751px]:items-stretch min-[751px]:gap-lg min-[751px]:p-2xl min-[1025px]:p-3xl">
        <FunctionCard.Title className="whitespace-pre-line text-violet-900" data-sentry-element="unknown" data-sentry-source-file="BoardLandingFeatures.tsx">
          {t('boardLanding:features.unlimited.title')}
        </FunctionCard.Title>
        <FunctionCard.Description data-sentry-element="unknown" data-sentry-source-file="BoardLandingFeatures.tsx">
          {t('boardLanding:features.unlimited.description')}
        </FunctionCard.Description>
      </div>

      <img src={getFeatureImage(locale, 'unlimited')} alt={t('boardLanding:features.unlimited.imageAlt')} width={620} height={230} loading="lazy" className="h-auto w-full" />
    </FunctionCard.VerticalWrapper>;
}
function QrCard() {
  const {
    t
  } = useTranslation(['boardLanding']);
  return (
    // Figma 는 이 카드만 시맨틱/스케일 변수 없이 raw hex `#f6f3fa` 를 바인딩했다
    // (purple-50 #f5edff, violet-50 #f3f2ff 어느 것과도 일치하지 않음) — 그대로 둔다.
    <FunctionCard.VerticalWrapper className="bg-[#F6F3FA]" data-sentry-element="unknown" data-sentry-component="QrCard" data-sentry-source-file="BoardLandingFeatures.tsx">
      <div className="flex flex-col items-center gap-sm px-lg py-xl min-[751px]:items-stretch min-[751px]:gap-lg min-[751px]:p-2xl min-[1025px]:p-3xl">
        <FunctionCard.Title className="whitespace-pre-line text-purple-900" data-sentry-element="unknown" data-sentry-source-file="BoardLandingFeatures.tsx">
          {t('boardLanding:features.qr.title')}
        </FunctionCard.Title>
        <FunctionCard.Description data-sentry-element="unknown" data-sentry-source-file="BoardLandingFeatures.tsx">
          {t('boardLanding:features.qr.description')}
        </FunctionCard.Description>
      </div>

      {/* QR·기기 목업이라 텍스트가 없어 로케일 분기가 없다. */}
      <img src={FEATURE_QR_IMAGE} alt={t('boardLanding:features.qr.imageAlt')} width={620} height={230} loading="lazy" className="h-auto w-full" />
    </FunctionCard.VerticalWrapper>
  );
}